import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useLazyGetFinancialStatisticsQuery,
  useLazyGetFinancialStatisticsBySearchIdQuery,
} from '@app/api/Analytics';
import {
  CALENDAR_PATH,
  INDIVIDUAL_CALENDAR_PATH,
  RECORDS_PATH,
  WARNING,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useHeader, useRecordModalContext } from '@app/providers';
import {
  useCalendarAPI,
  useIndividualCalendarDataRefresh,
} from '@app/providers/CalendarProvider/hooks';
import { useTeamCalendarContext } from '@app/providers/CalendarProvider/useCalendar';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NSearchIdProvider } from '@app/providers/SearchIdProvider/SearchIdProvider.types';

// Обновление страницы, с которой открыта запись, после её сохранения: журнал
// записей, журнал исполнителя, реестр записей и onAfterSave страницы. Нужно
// и окну записи, и окну тегов — теги сохраняются уже после закрытия окна
// записи, и без повторного обновления журнал показывает запись без них
// (issue/11332).
export const useRecordAfterSave = () => {
  const { pathname } = useLocation();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const { header } = useHeader();
  const { setIsNeedToUpdateRecordsRegistry, onAfterSaveRef } =
    useRecordModalContext();
  const { getEmployeesData } = useCalendarAPI();
  const { getEmployeeDataForTheSameQueryParams } =
    useIndividualCalendarDataRefresh();
  const { setSelectedEmployees, setAllEmployees, setFinancialStatistics } =
    useTeamCalendarContext();
  const [getFinancialStatistics] = useLazyGetFinancialStatisticsQuery();
  const [getFinancialStatisticsBySearchId] =
    useLazyGetFinancialStatisticsBySearchIdQuery();

  const getFinancialStatisticsBySearchIdHandler = useCallback(async () => {
    try {
      const response = await getFinancialStatisticsBySearchId({
        ActiveBranchId: activeBranchId,
        searchId: header?.searchId as NSearchIdProvider.SearchId,
      }).unwrap();

      setFinancialStatistics(response);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    showNotification,
    header,
    activeBranchId,
    getFinancialStatisticsBySearchId,
    setFinancialStatistics,
  ]);

  const afterSave = useCallback(
    async (date: string) => {
      switch (pathname) {
        case CALENDAR_PATH:
          if (!activeBranchId) return;
          const financialStatisticsData = await getFinancialStatistics({
            ActiveBranchId: activeBranchId,
            dateS: date,
            dateE: date,
          }).unwrap();
          setFinancialStatistics(financialStatisticsData);

          await getEmployeesData({
            date: date,
            onSuccess: (employeeDetails) => {
              setSelectedEmployees(employeeDetails);
              setAllEmployees(employeeDetails);
            },
          });

          break;
        case INDIVIDUAL_CALENDAR_PATH:
          await getEmployeeDataForTheSameQueryParams();
          break;
        case RECORDS_PATH:
          setIsNeedToUpdateRecordsRegistry(true);
          getFinancialStatisticsBySearchIdHandler();
          return;
        default:
          break;
      }

      await onAfterSaveRef.current();
    },
    [
      pathname,
      onAfterSaveRef,
      activeBranchId,
      getFinancialStatistics,
      setFinancialStatistics,
      getEmployeesData,
      getEmployeeDataForTheSameQueryParams,
      setIsNeedToUpdateRecordsRegistry,
      getFinancialStatisticsBySearchIdHandler,
      setSelectedEmployees,
      setAllEmployees,
    ]
  );

  return { afterSave };
};
